<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-base font-semibold text-gray-900">SMS Settings</h2>
      <p class="text-sm text-gray-500 mt-0.5">Configure auto-reply messages and default templates</p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 py-16 text-center">
      <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mb-3"></i>
      <p class="text-sm text-gray-400">Loading settings...</p>
    </div>

    <template v-else>
      <!-- SMS Provider -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">SMS Provider</h3>
        <p class="text-xs text-gray-400 mb-4">
          Only one provider can be active at a time. The active provider handles all outbound SMS,
          auto-replies and office forwards.
        </p>

        <div class="space-y-2 max-w-lg">
          <label
            v-for="p in providers"
            :key="p.value"
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
            :class="config.smsProvider === p.value
              ? 'border-indigo-400 bg-indigo-50'
              : 'border-gray-200 hover:bg-gray-50'"
          >
            <input type="radio" v-model="config.smsProvider" :value="p.value" class="mt-0.5" />
            <div class="text-left">
              <span class="text-sm font-medium text-gray-900">{{ p.label }}</span>
              <span v-if="p.value === 'twilio'"
                class="ml-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">Default</span>
              <p class="text-xs text-gray-400 mt-0.5">{{ p.helper }}</p>
            </div>
          </label>
        </div>

        <!-- Twilio -->
        <div v-if="config.smsProvider === 'twilio'" class="mt-5 pt-5 border-t border-gray-100 max-w-lg">
          <h4 class="text-xs font-semibold text-gray-700 mb-2 text-left">Twilio Settings</h4>
          <p class="text-xs text-gray-400 text-left">
            The sending number is configured under
            <router-link :to="{ name: 'SettingsCompany' }" class="text-indigo-500 hover:underline">Company Profile</router-link>
            as the Twilio From Number. Nothing else to configure here.
          </p>
        </div>

        <!-- SMS8 -->
        <div v-if="config.smsProvider === 'sms8'" class="mt-5 pt-5 border-t border-gray-100 max-w-lg">
          <h4 class="text-xs font-semibold text-gray-700 mb-3 text-left">SMS8 Settings</h4>
          <div class="space-y-4">
            <div>
              <label class="text-xs font-medium text-gray-500 block text-left mb-1">Device</label>
              <input v-model="config.sms8.device" type="text" placeholder="8248|1"
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors" />
              <p class="text-xs text-gray-400 mt-1 text-left">
                Format is <code class="bg-gray-100 px-1 rounded">deviceID|simSlot</code> &mdash; e.g. <code class="bg-gray-100 px-1 rounded">8248|1</code> for the Vinca device.
                Leave blank to let SMS8 choose.
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 block text-left mb-1">Phone Number</label>
              <input v-model="config.sms8.phoneNumber" type="text" placeholder="+61..."
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors" />
              <p class="text-xs text-gray-400 mt-1 text-left">
                The number on the SMS8 device, for reference. SMS8 sends from whichever device is selected above.
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 block text-left mb-1">Webhook URL</label>
              <div class="flex gap-2">
                <input :value="sms8WebhookUrl" type="text" readonly
                  class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 outline-none" />
                <button @click="copyWebhookUrl()" type="button"
                  class="flex-shrink-0 px-3 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <i class="fa-solid" :class="copied ? 'fa-check text-green-600' : 'fa-copy'"></i>
                </button>
              </div>
              <p class="text-xs text-gray-400 mt-1 text-left">Add this as the webhook URL in your SMS8 dashboard to receive inbound replies and delivery status.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Default Templates -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">Default Templates</h3>
        <p class="text-xs text-gray-400 mb-4">Select default templates for each SMS context</p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">Bulk SMS</label>
            <select v-model="config.defaultTemplates.bulk"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white">
              <option value="">None</option>
              <option v-for="t in bulkTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">Single Job SMS</label>
            <select v-model="config.defaultTemplates.single"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white">
              <option value="">None</option>
              <option v-for="t in singleTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">Client Book SMS</label>
            <select v-model="config.defaultTemplates.clientBook"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white">
              <option value="">None</option>
              <option v-for="t in clientBookTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Job Auto-Replies -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">Job SMS Auto-Replies</h3>
        <p class="text-xs text-gray-400 mb-4">Automatic responses when clients reply to job SMS notifications</p>
        <div class="space-y-4">
          <div v-for="key in jobReplyKeys" :key="key">
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">
              {{ jobReplyLabels[key] }}
            </label>
            <textarea v-model="config.autoReplyMessages.job[key]" rows="2"
              class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
          </div>
        </div>
      </div>

      <!-- Tester Notifications -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">Tester Notifications</h3>
        <p class="text-xs text-gray-400 mb-4">Messages sent to clients when testers trigger notifications</p>
        <div class="space-y-4">
          <div>
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">
              En-Route Notification
            </label>
            <p class="text-xs text-gray-400 mb-1.5">Sent to the client when a tester marks themselves as en-route. Shortcodes: {tester}, {company}, {time}, {address}, {contact}, {link}</p>
            <textarea v-model="config.autoReplyMessages.enRoute" rows="2"
              class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
          </div>
        </div>
      </div>

      <!-- Marketing Auto-Replies -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-sm font-semibold text-gray-900 mb-1">Marketing SMS Auto-Replies</h3>
        <p class="text-xs text-gray-400 mb-4">Automatic responses when clients reply to marketing/bulk SMS messages</p>
        <div class="space-y-4">
          <div v-for="key in marketingReplyKeys" :key="key">
            <label class="text-xs font-medium text-gray-500 block text-left mb-1">
              {{ marketingReplyLabels[key] }}
            </label>
            <textarea v-model="config.autoReplyMessages.marketing[key]" rows="2"
              class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
          </div>
        </div>
      </div>

      <!-- Save -->
      <div class="flex justify-end">
        <button @click="save()" :disabled="saving"
          class="inline-flex items-center px-5 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors disabled:opacity-50"
          style="height:40px">
          <i v-if="saving" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

      <!-- Success message -->
      <div v-if="saved" class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <i class="fa-solid fa-circle-check text-green-600"></i>
        <span class="text-sm text-green-700 font-medium">Settings saved successfully</span>
      </div>
    </template>
  </div>
</template>

<script>
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings";

const SMS8_WEBHOOK_URL = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/sms8/incoming";

const DEFAULT_JOB_REPLIES = {
  FIRM: 'Thanks, your job has been confirmed.',
  CHANGED: "Thanks, we've forwarded your change request to the team.",
  CANCELLED: "Thanks, we've noted your cancellation request.",
};

const DEFAULT_MARKETING_REPLIES = {
  new_job: "Thanks! We've noted your job request and our team will be in touch to confirm details.",
  cancel_job: "Thanks, we've noted your cancellation request.",
  change_job: "Thanks, we've forwarded your change request to the team.",
  question: "Thanks for your message. Our team will get back to you shortly.",
  complaint: "Thanks for letting us know. A team member will follow up with you.",
  negative: "No worries, thanks for letting us know!",
  positive: "Thanks for confirming!",
};

export default {
  data() {
    return {
      loading: false,
      saving: false,
      saved: false,
      copied: false,
      templates: [],
      sms8WebhookUrl: SMS8_WEBHOOK_URL,
      providers: [
        { value: 'twilio', label: 'Twilio', helper: 'Sends via the Twilio API using the number set in Company Profile.' },
        { value: 'sms8', label: 'SMS8', helper: 'Sends via an Android device registered with SMS8.' },
      ],
      config: {
        smsProvider: 'twilio',
        sms8: { device: '8248|1', phoneNumber: '' },
        defaultTemplates: { bulk: '', single: '', clientBook: '' },
        autoReplyMessages: {
          job: { ...DEFAULT_JOB_REPLIES },
          marketing: { ...DEFAULT_MARKETING_REPLIES },
          enRoute: 'Hi, your tester {tester} is now en-route to your site. Expected arrival based on your scheduled time: {time}.',
        },
      },
      jobReplyKeys: ['FIRM', 'CHANGED', 'CANCELLED'],
      jobReplyLabels: {
        FIRM: 'Confirmed (FIRM)',
        CHANGED: 'Change Requested (CHANGED)',
        CANCELLED: 'Cancelled (CANCELLED)',
      },
      marketingReplyKeys: ['new_job', 'cancel_job', 'change_job', 'question', 'complaint', 'negative', 'positive'],
      marketingReplyLabels: {
        new_job: 'New Job Request',
        cancel_job: 'Cancellation',
        change_job: 'Change Request',
        question: 'Question',
        complaint: 'Complaint',
        negative: 'Negative Response',
        positive: 'Positive Response',
      },
    };
  },
  computed: {
    bulkTemplates() {
      return this.templates.filter(t => t.type === 'bulk' || t.type === 'all');
    },
    singleTemplates() {
      return this.templates.filter(t => t.type === 'single' || t.type === 'all');
    },
    clientBookTemplates() {
      return this.templates.filter(t => t.type === 'clientBook' || t.type === 'all');
    },
  },
  methods: {
    getCurrentUser() {
      return new Promise((resolve, reject) => {
        if (auth.currentUser) return resolve(auth.currentUser);
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          user ? resolve(user) : reject(new Error('Not authenticated'));
        });
      });
    },
    async getHeaders() {
      const user = await this.getCurrentUser();
      const token = await user.getIdToken();
      return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    },
    async fetchData() {
      this.loading = true;
      try {
        const headers = await this.getHeaders();
        const [configRes, templatesRes] = await Promise.all([
          fetch(`${API_BASE}/sms`, { headers }),
          fetch(`${API_BASE}/sms/templates`, { headers }),
        ]);
        const configJson = await configRes.json();
        const templatesJson = await templatesRes.json();

        this.templates = templatesJson.data || [];

        const data = configJson.data || {};
        // Anything other than the exact string 'sms8' falls back to Twilio,
        // matching the backend so the UI never misreports the active provider.
        this.config.smsProvider = data.smsProvider === 'sms8' ? 'sms8' : 'twilio';
        if (data.sms8) {
          this.config.sms8 = { ...this.config.sms8, ...data.sms8 };
        }
        if (data.defaultTemplates) {
          this.config.defaultTemplates = { ...this.config.defaultTemplates, ...data.defaultTemplates };
        }
        if (data.autoReplyMessages) {
          if (data.autoReplyMessages.job) {
            this.config.autoReplyMessages.job = { ...DEFAULT_JOB_REPLIES, ...data.autoReplyMessages.job };
          }
          if (data.autoReplyMessages.marketing) {
            this.config.autoReplyMessages.marketing = { ...DEFAULT_MARKETING_REPLIES, ...data.autoReplyMessages.marketing };
          }
          if (data.autoReplyMessages.enRoute) {
            this.config.autoReplyMessages.enRoute = data.autoReplyMessages.enRoute;
          }
        }
      } catch (e) {
        console.error('Failed to load SMS config:', e);
      }
      this.loading = false;
    },
    copyWebhookUrl() {
      const done = () => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.sms8WebhookUrl).then(done).catch(() => {});
      }
    },
    async save() {
      this.saving = true;
      this.saved = false;
      try {
        const headers = await this.getHeaders();
        await fetch(`${API_BASE}/sms`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(this.config),
        });
        this.saved = true;
        setTimeout(() => { this.saved = false; }, 3000);
      } catch (e) {
        console.error('Failed to save SMS config:', e);
      }
      this.saving = false;
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
